/**
 * `/fly`'s side of BEST MISSIONS, as one object — the same shape
 * `vcr/flight-recording.ts` uses for SAVE FLIGHT, because it is the same
 * moment: a sortie just ended, and there is a name to ask for before the
 * flight leaves the screen.
 *
 * **Eligibility.** Only a single mission counts: a career sortie is credited
 * to the pilot's own record instead (`fly-target.ts`'s `credit`, landing in
 * BEST CAREERS/ALL TIME HIGH SCORE, not here), and the bare instant dogfight
 * (`/fly` with no query at all) is not one of the Fly Single Mission catalog
 * types the original's grid offers, so it never had a Best Missions entry to
 * make either. `mission !== undefined && pilotId === undefined` is exactly
 * `fly-target.ts`'s own single-mission branch.
 *
 * **Generic on purpose.** This reads nothing off `mission` but its title and
 * flown aircraft, and nothing off the debrief but its score — so a sibling
 * lane's mission type scores here identically without this module knowing it
 * exists, the same discipline `best-missions.ts` documents at the engine layer.
 */

import { createSignal, type Accessor } from "solid-js";
import {
  aircraftLabel,
  playerFlightOf,
  qualifiesForBestMissions,
  type BestMissionEntry,
  type MissionDebrief,
  type MissionSpec,
} from "@red-opus/engine";
import { browserBestMissionsStore, loadBestMissions, recordBestMission } from "./best-missions-store.ts";

export interface BestMissionRecording {
  /** Whether the name prompt is up. */
  readonly prompting: Accessor<boolean>;
  /** This entry's 1-based place on the table, once it has been recorded. */
  readonly rank: Accessor<number | undefined>;
  /** The name it was recorded under, once it has been. */
  readonly savedAs: Accessor<string | undefined>;
  /** Wire straight to the debrief callback: checks eligibility and pops the prompt when the score qualifies. A no-op otherwise. */
  readonly credit: (debrief: MissionDebrief) => void;
  readonly save: (name: string) => void;
  readonly skip: () => void;
}

export function createBestMissionRecording(mission: MissionSpec | undefined, pilotId: string | undefined): BestMissionRecording {
  const store = browserBestMissionsStore();
  const eligible = mission !== undefined && pilotId === undefined;
  const plane = mission ? aircraftLabel(playerFlightOf(mission).aircraft) : "";
  const title = mission?.title ?? "";

  const [prompting, setPrompting] = createSignal(false);
  const [rank, setRank] = createSignal<number | undefined>(undefined);
  const [savedAs, setSavedAs] = createSignal<string | undefined>(undefined);
  let pendingScore: number | undefined;

  return {
    prompting,
    rank,
    savedAs,
    credit: (debrief) => {
      if (!eligible || !store) return;
      if (!qualifiesForBestMissions(loadBestMissions(store), debrief.missionScore)) return;
      pendingScore = debrief.missionScore;
      setPrompting(true);
    },
    save: (name) => {
      if (!store || pendingScore === undefined) return;
      const entry: BestMissionEntry = {
        name: name.trim() || "Pilot",
        missionTitle: title,
        plane,
        score: pendingScore,
        recordedAt: new Date().toISOString(),
      };
      const placement = recordBestMission(store, entry);
      setRank(placement.rank);
      setSavedAs(entry.name);
      setPrompting(false);
      pendingScore = undefined;
    },
    skip: () => {
      setPrompting(false);
      pendingScore = undefined;
    },
  };
}
